import { TimeoutError } from '@repo/types-client';

/**
 * Creates a promise that rejects after a specified timeout.
 * @function getTimeout
 * @param {number} timeoutInSeconds - The timeout duration in seconds.
 * @param {string} [errorMessage='Promise timeout'] - The error message for the timeout.
 * @returns {Promise<void>} A promise that rejects with a TimeoutError after the specified timeout.
 */
const getTimeout = (
  timeoutInSeconds: number,
  errorMessage = 'Promise timeout',
) =>
  new Promise<void>((resolve, reject) => {
    const interval = setTimeout(
      () => reject(new TimeoutError(errorMessage)),
      timeoutInSeconds * 1000,
    );
    if (!interval.unref) return;
    interval.unref();
  });

/**
 * Adds a timeout to a promise, rejecting it if it does not resolve within the specified time.
 * @function timeout
 * @template T - The type of the promise result.
 * @param {Promise<T>} promise - The promise to add a timeout to.
 * @param {object} [options] - The options for the timeout.
 * @param {number} [options.timeoutInSeconds=30] - The timeout duration in seconds.
 * @param {string} [options.errorMessage='Promise race timeout'] - The error message for the timeout.
 * @param {Promise<T>} [options.raceCandidate] - An optional promise to race against.
 * @returns {Promise<T>} A promise that resolves with the result of the original promise or rejects with a TimeoutError.
 */
const timeout = async <T>(
  promise: Promise<T>,
  options?: {
    timeoutInSeconds?: number;
    errorMessage?: string;
    raceCandidate?: Promise<T>;
  },
): Promise<T> => {
  const errorMessage = options?.errorMessage ?? 'Promise race timeout';
  const timeoutInSeconds = options?.timeoutInSeconds ?? 30;

  const raceCandidate: Promise<T> =
    options?.raceCandidate ??
    (getTimeout(timeoutInSeconds, errorMessage) as Promise<T>);

  return Promise.race([promise, raceCandidate]);
};

export { timeout };
